import { useEffect, useState } from 'react';
import {
  QUICK_WITHDRAWAL_STATISTICS_RANGES,
  WITHDRAWAL_STATISTICS_RANGE_LABELS,
  useWithdrawalStatistics,
} from '../features/withdrawal-statistics';
import type { WithdrawalStatisticsRange, WithdrawalStatisticsRowDTO } from '../features/withdrawal-statistics';

function StatisticsRow({ item }: { item: WithdrawalStatisticsRowDTO }) {
  return (
    <tr className="border-b border-gray-200 hover:bg-gray-50 transition-colors">
      <td className="px-4 py-3 text-gray-900 whitespace-nowrap">{item.date}</td>
      <td className="px-4 py-3 text-gray-900 whitespace-nowrap">{item.channel_label}</td>
      <td className="px-4 py-3 text-right tabular-nums text-gray-900">{item.withdrawal_count}</td>
      <td className="px-4 py-3 text-right tabular-nums text-gray-900">&yen;{item.total_amount_yuan}</td>
      <td className="px-4 py-3 text-right tabular-nums text-emerald-700">&yen;{item.success_amount_yuan}</td>
      <td className="px-4 py-3 text-right tabular-nums text-red-700">&yen;{item.failed_amount_yuan}</td>
      <td className="px-4 py-3 text-right tabular-nums text-amber-700">&yen;{item.pending_amount_yuan}</td>
    </tr>
  );
}

function SummaryCard({
  label,
  value,
  tone = 'default',
}: {
  label: string;
  value: string;
  tone?: 'default' | 'success' | 'danger' | 'warning';
}) {
  const toneClass = {
    default: 'text-gray-900',
    success: 'text-emerald-700',
    danger: 'text-red-700',
    warning: 'text-amber-700',
  }[tone];

  return (
    <div className="rounded-md border border-gray-200 bg-white px-4 py-3">
      <div className="text-xs text-gray-500">{label}</div>
      <div className={`mt-1 text-lg font-semibold tabular-nums ${toneClass}`}>{value}</div>
    </div>
  );
}

export function WithdrawalStatisticsPage() {
  const {
    data,
    loading,
    error,
    range,
    startDate,
    endDate,
    channel,
    setRange,
    setCustomDateRange,
    setChannel,
    resetFilters,
    reload,
  } = useWithdrawalStatistics();
  const [customStartDate, setCustomStartDate] = useState(startDate);
  const [customEndDate, setCustomEndDate] = useState(endDate);
  const selectedChannel = channel || 'all';

  useEffect(() => {
    setCustomStartDate(startDate);
    setCustomEndDate(endDate);
  }, [endDate, startDate]);

  const handleQuickRange = (value: WithdrawalStatisticsRange) => {
    setRange(value);
  };

  const handleApplyCustomRange = () => {
    setCustomDateRange(customStartDate, customEndDate);
  };

  const handleReset = () => {
    resetFilters();
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">
        成功/失败按完成时间统计，待处理按申请时间统计。
      </p>

      <div className="flex items-center gap-3 flex-wrap">
        <select
          aria-label="出款渠道"
          value={selectedChannel}
          onChange={(e) => setChannel(e.target.value === 'all' ? '' : e.target.value)}
          className="h-9 rounded-md border border-gray-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
        >
          {(data?.channels ?? [{ key: 'all', label: '全部渠道' }]).map((item) => (
            <option key={item.key} value={item.key}>
              {item.label}
            </option>
          ))}
        </select>

        <div className="flex items-center gap-1 rounded-md border border-gray-200 bg-gray-50 p-1">
          {QUICK_WITHDRAWAL_STATISTICS_RANGES.map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => handleQuickRange(value)}
              className={`h-7 rounded px-3 text-sm transition-colors ${
                range === value
                  ? 'bg-white text-primary shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {WITHDRAWAL_STATISTICS_RANGE_LABELS[value]}
            </button>
          ))}
        </div>

        <input
          aria-label="自定义开始日期"
          type="date"
          value={customStartDate}
          onChange={(e) => setCustomStartDate(e.target.value)}
          className="h-9 rounded-md border border-gray-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
        />
        <span className="text-sm text-gray-400">至</span>
        <input
          aria-label="自定义结束日期"
          type="date"
          value={customEndDate}
          onChange={(e) => setCustomEndDate(e.target.value)}
          className="h-9 rounded-md border border-gray-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
        />
        <button
          type="button"
          onClick={handleApplyCustomRange}
          className={`h-9 rounded-md px-3 text-sm transition-colors ${
            range === 'custom'
              ? 'bg-primary text-white'
              : 'border border-gray-300 text-gray-600 hover:bg-gray-50'
          }`}
        >
          自定义
        </button>

        <button
          type="button"
          onClick={handleReset}
          className="h-9 px-3 text-sm text-gray-500 hover:text-gray-700 transition-colors"
        >
          重置
        </button>
        <button
          type="button"
          onClick={reload}
          className="h-9 px-3 text-sm text-gray-500 hover:text-gray-700 transition-colors"
        >
          刷新
        </button>
      </div>

      {data && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <SummaryCard label="出款笔数" value={String(data.summary.withdrawal_count)} />
          <SummaryCard label="出款总金额" value={`¥${data.summary.total_amount_yuan}`} />
          <SummaryCard label="成功金额" value={`¥${data.summary.success_amount_yuan}`} tone="success" />
          <SummaryCard label="失败金额" value={`¥${data.summary.failed_amount_yuan}`} tone="danger" />
          <SummaryCard label="待处理金额" value={`¥${data.summary.pending_amount_yuan}`} tone="warning" />
        </div>
      )}

      {error && (
        <div className="py-10 text-center">
          <p className="text-sm text-red-500 mb-3">{error}</p>
          <button
            type="button"
            onClick={reload}
            className="text-sm text-primary hover:underline"
          >
            重试
          </button>
        </div>
      )}

      {loading && !error && (
        <div className="flex items-center justify-center py-20">
          <div className="h-6 w-6 border-2 border-gray-300 border-t-primary rounded-full animate-spin" />
        </div>
      )}

      {!loading && !error && (
        <>
          {!data || data.items.length === 0 ? (
            <div className="py-20 text-center text-sm text-gray-400">
              当前筛选条件下暂无出款统计数据。
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      日期
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      出款渠道
                    </th>
                    <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      出款笔数
                    </th>
                    <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      出款总金额
                    </th>
                    <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      成功金额
                    </th>
                    <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      失败金额
                    </th>
                    <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      待处理金额
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((item) => (
                    <StatisticsRow key={`${item.date}-${item.channel}`} item={item} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
